/**
 * Precedent: APR Framework Builder — `apps/portal/web/src/pages/apr/settings/FrameworkBuilder.tsx`
 * (+ `FrameworkBuilder.css:96-140`, `:303-324`). Cloned, not imported (DECISIONS.md D-003).
 * Nothing under pages/apr/ or pages/master-data/ is modified by this module.
 *
 * SCREEN_MAP #5 — CAP-01-06, and **§6.1's 100% rule is the heart of it**.
 *
 * ── THE TREE IS NOW A SHARED COMPONENT, AND NOTHING ABOUT IT CHANGED ───────────────────
 *
 * The indented table — its columns, its toggles, its inline names, its weight cells, its group
 * total rows with the running-total bar and the two helpers — lives in `WeightedTree.tsx`, and the
 * rules that style it in `WeightedTree.css`. It MOVED there, element for element and value for
 * value, when the capability template library needed the same tree: a template's lines are the
 * same object one step earlier (`0094`: *"Arbitrary depth, same tree shape as capability. A
 * top-level line is a functional area."*), and the contract specifies the two weight endpoints as
 * *"identical in shape and semantics"* (§6.7), which the server honours by running one validator
 * over both tables. A second transcription of the screen around that would have been the third
 * copy of a rule D-009 requires to exist once.
 *
 * WHAT STAYED HERE is everything that is this screen's and not the tree's: the initiative and
 * version pickers, the models table, the gate strip and the Baseline action, every API call, the
 * confirmation before a delete, the panels and the toasts. The component holds one piece of state —
 * which rows are collapsed — and writes nothing.
 *
 * WHAT IS CLONED FROM THE PRECEDENT: the toolbar on its own row below the heading with the
 * selector written into the URL (`:933-949` — "writing the choice into the URL rather than into
 * state keeps `?framework=` the single source of truth, so the load effect that already keys on
 * it does the reload, and a shared link still opens the same framework"); the blue ribbon naming
 * what is being edited (`:986-990`); the fixed-layout grouped table with its sticky header and
 * its tinted double-ruled first row per group; inline editable text saved on blur; the 🗑️ delete
 * glyph; and save-as-you-go.
 *
 * ── WHAT IS NO LONGER CLONED: THE LOCK RULE. IT IS A WARNING NOW, NOT A REFUSAL ────────
 *
 * The precedent's `:40` — *"A framework already in use by an APR run is **locked** from structural
 * change so existing scores stay stable"* — was reproduced here as a baselined model being
 * READ-ONLY, because API_CONTRACT §6.11-§6.17 all answered `409 capability_model_is_baselined` and
 * `POST /versions` was the only way to change one.
 *
 * **The owner has ruled that refusal away** (2026-10-05, API_CONTRACT §6.19):
 *
 * > *"when I am in model, I need to have ability to modify it as long as it has not been used. When
 * > used, then any change has to come with warning that model has been used and that any change
 * > will have downstream impact on recalculation"*
 *
 * All five endpoints now SAVE, and each returns `warnings` — `[]` when there is nothing to say. So a
 * baselined model is edited where it is: add a functional area, add a capability, rename, reweight,
 * delete a row, exactly as on a draft. **Create new version STAYS**, because superseding is still a
 * legitimate choice; it is no longer the only one.
 *
 * **THE PREDICATE IS USAGE, NOT STATUS**, and that half is the one easiest to get wrong by adding
 * something reassuring. The owner: *"If that model hasn't been used, then I should see no impact at
 * all."* An unused model — draft or baselined — shows NO banner, NO confirmation and no toast about
 * consequences: the edit just saves, and nothing on the way to the server is added to it. Where the
 * model HAS been used, `?dryRun=1` previews the same `warnings` the commit would return and they are
 * shown as a confirmation BEFORE the write, in the server's own words and with its own counts
 * (§6.19: a second code path computing that sentence is a second implementation that can disagree
 * with the one that commits — and the dialog and the audit trail would word one change two ways).
 *
 * WHAT IS NOT CLONED, and why — the long form is in `CapabilityModelEditor.css`'s header:
 *   1. ONE INDENTED TREE COLUMN instead of three level columns, because a capability tree is
 *      unbounded (`0094`) and UI_GAPS.md §4 records that the precedent's layout "cannot express
 *      depth 4". Indent is `16 + depth * 16` (`AppShell.tsx:165-166`).
 *   2. NO `WeightDots`. UI_CONVENTIONS §8.4: a 1-5 integer control "can express neither a
 *      capability weight to one decimal place summing to exactly 100.0 nor a 1-100% value — so
 *      the dots are a precedent for NOTHING here". The control is a numeric cell with PEPM's
 *      `.pm-ov-input` metrics.
 *   3. NOT A BARE `<select>` ANYWHERE A RECORD IS PICKED. **D-014** — the owner has ruled the
 *      initiative and the version are Project/Opportunity-class fields, so the toolbar's two
 *      (`:933-949`) are the platform's `SelectField`, and with them the dialogs' **Parent** and
 *      **Start from a template**, which pick a record too. The long notes sit at the call sites;
 *      what is still cloned from the precedent is that the toolbar's choice is written into the
 *      URL and nowhere else.
 *
 *      **THE CONVENTION DOCUMENT WAS RIGHT AND THE CLONE WAS THE DEVIATION.** This is worth
 *      knowing before reading the rest of this file: `UI_CONVENTIONS.md` §5.2 already named
 *      `SelectField` as this platform's select ("an owner-ruled design"), and `SCREEN_MAP.md:160`
 *      already marked fields and pickers **Import**. So D-014 did not overrule a convention — it
 *      RESTORED one that the faithful clone of a bare `<select>` had quietly broken. Operating
 *      mode (`InitiativeDetailPanel.tsx:404`) stays a native `<select>` on purpose: a two-value
 *      enum is not a record, and D-014 is scoped to records.
 *
 * ── THE 100% RULE, AND THE FOUR THINGS THAT GO WRONG QUIETLY ───────────────────────────
 *
 * INTEGER TENTHS, NEVER FLOATS (§1.4). Every weight on the wire and in state is an integer number
 * of tenths; `1000` is 100.0%. A float balance check refuses a valid model — A8 has the failing
 * case — so nothing here adds a percentage to a percentage. `weights.ts` owns the arithmetic and
 * is the same implementation the server runs (§3.1: "a rule implemented twice is a rule that will
 * eventually disagree with itself"), which is why the sentence beside a total while you type is
 * byte-identical to the sentence in the 409 if you baseline anyway.
 *
 * SAVE IS ALLOWED, BASELINE IS NOT (§3.1). An unbalanced set saves as draft and comes back with
 * the deficit; the baseline action is disabled with the reason shown, and the server refuses it
 * independently.
 *
 * NEVER REDISTRIBUTE SILENTLY (§6.1, §3.1's table). Adding a capability gives it 0.0 — the server
 * refuses a weight on create outright (§6.14), so none is sent. Removing one leaves its weight
 * unallocated and the group under by that amount. Moving one leaves the old group under and the
 * new one over, and the response reports BOTH. Nothing on this screen rebalances behind the user;
 * the two helpers are explicit buttons, and they are the only things that change a weight the
 * user did not type.
 *
 * EFFECTIVE WEIGHT IS THE SERVER'S (§3.3). It is the product down the tree, floored at each
 * level, computed once on read and REFUSED as input (`400 computed_field_not_writable`). This
 * screen renders the field it was given and never recomputes it — the live total beside a weight
 * is a sibling SUM, which is a different quantity.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
/**
 * D-014 — every picker on this screen that chooses a RECORD (initiative, version, parent
 * capability, template) is the platform's one record-picker field. See the long note at the
 * toolbar for why this import is correct and is not a D-003 breach.
 */
/**
 * `ConfirmDialog` is the platform's ONE confirmation, imported rather than cloned — the same choice
 * `businessUnits/BusinessUnitHierarchy.tsx:49` made, and D-017's rule: a form is a right-side panel,
 * a yes-or-no question about an action already chosen stays a CENTRED DIALOG, because it must
 * interrupt.
 */
import { ConfirmDialog, SelectField } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { ERP_PROMPT } from '../prompts';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { FormPanel } from '../chrome/FormPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
/* `ErpApiError` went with the Baseline handler — it caught that endpoint's
   `weights_do_not_balance` to paint the server's per-group sentences. */
import { erpApi } from '../api';
import {
  type BaselinedModelUnbalancedWarning,
  type Capability,
  type CapabilityCreate,
  type CapabilityModelDeletePreview,
  type CapabilityModelEditPreview,
  type CapabilityModelTreePayload,
  type CapabilityModelUsage,
  type CapabilityModelVersionRow,
  type CapabilityModelWarning,
  type CapabilityPatch,
  type CapabilityTemplate,
  type ErpContext,
  type Initiative,
  type WeightBalanceReport,
} from '../types';
import { canDeleteCapabilityModel, canEditCapabilityModel } from '../roles';
/**
 * THE 100% RULE COMES FROM `@platform/erp-scoring`, through this module's `weights.ts`, which is a
 * re-export of it (D-009: *"a rule implemented twice is a rule that will eventually disagree with
 * itself"*). The server runs the same functions. Nothing on this screen implements the arithmetic.
 */
import {
  describeBalance,
  isWeightInRange,
  orderTree,
  summariseBalance,
  textToTenths,
  type WeightChange,
  type WeightNode,
} from '../weights';
/**
 * THE TREE ITSELF IS A SHARED COMPONENT — `WeightedTree.tsx`, which this screen and the capability
 * template library both render. The markup, the classes and every CSS value are the clone of APR's
 * Framework Builder that this file already carried; they MOVED there when the template library
 * needed the same tree, rather than being transcribed a second time. That component's header
 * carries the long form of why, and `WeightedTree.css` carries the mechanical proof that no rule
 * was lost on the way across.
 */
import { describeKeyProblem, draftKey, finishKey, suggestKeyFromName } from '../keys';
import { WeightedTree, type WeightedTreeRow } from './WeightedTree';
import '../chrome/recordList.css';
import './CapabilityModelEditor.css';

/* ---------------------------------------------------------------------------
 * Contextual help — AUTHORED from `BUILD_INSTRUCTIONS.md` §6.1 (UI_CONVENTIONS §3.7).
 * ------------------------------------------------------------------------- */

const CapabilityModelHelp: Help = {
  title: 'Capability Model',
  icon: '🧮',
  purpose:
    'The <strong>capability model</strong> is the tree every system is weighed against: <strong>functional areas</strong> at the top, capabilities within them, sub-capabilities within those. Each level carries a <strong>weight</strong>, and the weights decide how much each part of the business counts toward the result.',
  doHere: [
    'Pick an <strong>initiative</strong> and one of its model <strong>versions</strong>. The newest version is selected by default.',
    'Add a <strong>functional area</strong>, then add capabilities beneath it. A new capability always starts at <strong>0.0%</strong> — nothing is taken from its siblings to pay for it.',
    'Type each <strong>weight</strong> to one decimal place. The running total for that set of siblings updates as you type, and the weight saves when you leave the cell.',
    'Use <strong>Distribute evenly</strong> to give every unlocked sibling an equal share, or <strong>Normalize proportionally</strong> to keep their relative sizes and make them add up.',
    '<strong>Lock</strong> a weight to hold it still: both helpers leave a locked weight exactly as it is and share out only what is left.',
    'When <strong>every</strong> level totals exactly 100.0%, <strong>Baseline model</strong> fixes it so scores computed against it stay stable.',
    'A <strong>baselined</strong> model can still be changed here. If it has already been used, the change is shown to you first — with the number of scores it moves — and saves only when you confirm.',
    'Delete a model from the <strong>models table</strong>. The bin appears only on a model nothing depends on — one that has been scored against is superseded by a new version instead, which is what keeps the earlier scores readable.',
  ],
  fits: 'ERP Consolidation &rarr; Capability Model',
  firstTime:
    'Add two or three functional areas, then use Distribute evenly on them — that is the fastest way to a model that balances, and you can adjust from there.',
  sections: [
    {
      type: 'defs',
      label: 'The 100% rule',
      items: [
        {
          term: 'Exactly 100.0',
          color: '#0d652d',
          def: 'Valid. Every <strong>sibling set</strong> must total this — the functional areas together, and the children of each capability together.',
        },
        {
          term: 'Under 100',
          color: '#3c4043',
          def: 'Shown as <strong>"x% left to allocate"</strong>. The model still <strong>saves as a draft</strong> — it just cannot be baselined yet.',
        },
        {
          term: 'Over 100',
          color: '#8c1d18',
          def: 'Shown as <strong>"x% over"</strong>. Also saves; also blocks baselining.',
        },
      ],
      note: 'Weights are held to one decimal place, and the check is exact. A capability with no children is not a set and is never itself a violation.',
    },
    {
      type: 'defs',
      label: 'Model state',
      items: [
        {
          term: 'Draft',
          color: '#3c4043',
          def: 'Fully editable. An unbalanced draft is a normal state, not an error.',
        },
        {
          term: 'Baselined',
          color: '#8c3a00',
          def: 'Fixed as the thing earlier scores were computed on. <strong>Still editable</strong> — a change saves here, and if the model has been used you are told what it moves before it does. Creating a <strong>new version</strong> is the other choice: it copies the tree into a new draft and leaves this one exactly as it is.',
        },
      ],
    },
    {
      type: 'html',
      label: 'Two numbers that are not the same',
      html: 'The <strong>weight</strong> is a share of its own sibling set. <strong>Effective</strong> is that weight multiplied down the tree — a capability at 40.0% inside an area at 25.0% is 10.0% of the whole model. Effective is computed on the server when the model is read, so it updates when a weight is saved rather than while it is being typed.',
    },
    {
      type: 'html',
      label: 'What this screen will not do for you',
      html: 'It never rebalances a group behind your back. Adding a capability gives it 0.0%; removing one leaves its weight unallocated. If a total needs fixing, one of the two helpers does it — visibly, and only when you ask.',
    },
  ],
};

type Toast = { type: 'success' | 'error'; message: string } | null;

/**
 * A write that is waiting on a yes.
 *
 * ONE state and ONE dialog for every confirmation on this screen — the edit warnings, the
 * capability delete and the model delete — because they are the same object: a sentence, the
 * server's warnings if it has any, and the thing to do if the answer is yes. Three dialog states
 * would be three places to get the busy flag and the cancel path wrong.
 *
 * `commit` is the write itself, held until confirmed. `undo` puts the screen back if the answer is
 * no: a weight is typed into local state before it is sent (§3.1's live total), so cancelling has
 * to return it.
 */
interface PendingWrite {
  readonly title: string;
  /** This screen's own sentence about what is about to happen, when there is one to say. */
  readonly intro?: string;
  readonly confirmLabel: string;
  readonly destructive?: boolean;
  /** The SERVER's warnings, rendered one block each and never merged into one sentence. */
  readonly warnings: readonly CapabilityModelWarning[];
  readonly commit: () => Promise<void>;
  readonly undo?: () => void;
}

/*
 * The balance warning is pulled OUT of `warnings` because it is shown somewhere else — a band in the
 * gate strip rather than a question in the dialog (§6.19 keeps the two facts apart). The in-use
 * warning needs no such helper: where `usage.used` is true the server always sends it, so the dialog
 * renders whatever came back, in the order it came back in, which puts it first.
 */
const unbalancedWarningOf = (
  warnings: readonly CapabilityModelWarning[],
): BaselinedModelUnbalancedWarning | null =>
  warnings.find(
    (warning): warning is BaselinedModelUnbalancedWarning =>
      warning.warning === 'baselined_model_no_longer_balances',
  ) ?? null;

/*
 * ── A BLOCKED ROW SHOWS NOTHING. NOT A NOTE, NOT A GREYED BIN. ─────────────────────────
 *
 * This screen briefly carried a short summary of the server's blockers in the actions cell — "1 later
 * version" — on the template library's "in use by 2" pattern. The owner saw it and ruled it out,
 * 2026-10-05: *"please remove '1 later version' from screen Capability model. I do not need to see
 * that."*
 *
 * So the rule here is the module's own, UI_CONVENTIONS §17.3: **HIDDEN, NOT DISABLED** — and now not
 * annotated either. Where §6.20's preview says the delete would be refused, the bin is simply not
 * rendered, exactly as the initiatives list omits Edit and Delete on a baselined initiative.
 *
 * WHAT MAKES THE HIDING HONEST rather than mysterious: the delete path still shows the server's own
 * refusal if it is ever reached — by a stale preview, a race, or a role that changed while the screen
 * was open. That handling in `askDeleteModel` is the fallback, and it is why nothing has to be
 * explained in the table.
 *
 * The template library's own annotation is UNTOUCHED. The ruling named this screen, and CLAUDE.md's
 * additive rule is explicit that a change to one thing is not permission to change another.
 */

export function CapabilityModelEditor() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setRoot, rootStyle } = useScreenLock();

  const initiativeId = searchParams.get('initiative') ?? '';
  const modelParam = searchParams.get('model');

  const [context, setContext] = useState<ErpContext | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [versions, setVersions] = useState<CapabilityModelVersionRow[]>([]);
  const [tree, setTree] = useState<CapabilityModelTreePayload | null>(null);
  const [serverBalance, setServerBalance] = useState<WeightBalanceReport | null>(null);

  /** Weights in TENTHS, re-seeded from every server response (§5.3's rule). */
  const [weights, setWeights] = useState<Record<string, number>>({});
  /** The cell being typed in — raw text, so "4." and "" are expressible. */
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  /* Which rows are COLLAPSED is `WeightedTree`'s own state: it is presentation, and nothing on
     this screen reads it. */

  const [loading, setLoading] = useState(true);
  const [treeLoading, setTreeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  const [newCapability, setNewCapability] = useState<{
    parent_capability_id: string;
    name: string;
    code: string;
  } | null>(null);
  /*
   * A NEW MODEL CARRIES A DESCRIPTION — owner, 2026-10-05: *"please enable the ability to create a
   * version description … so when I add new model, also add ability to create a version"*.
   *
   * `POST /initiatives/:id/capability-models` has always accepted `description`; this panel sent
   * `description: null` outright, so the field could only be filled afterwards through the
   * attributes panel. Since a model row IS a version (`capability_model.description` is per row),
   * this field and the one in the New Version panel below are the same field, and no schema change
   * was needed for either.
   */
  const [newModel, setNewModel] = useState<{
    initiativeId: string;
    /** `0105` — the model's own key, required on create. */
    modelKey: string;
    name: string;
    description: string;
    templateId: string;
  } | null>(null);
  const [templates, setTemplates] = useState<CapabilityTemplate[]>([]);
  /*
   * WHY THE PANEL CARRIES ITS OWN ERROR, rather than raising a toast.
   *
   * A toast for "Key is required" is the wrong instrument twice over: it appears in the opposite
   * corner from the field at fault, and it says nothing about WHICH field. The eye is in the
   * panel, so the message belongs in the panel. The toast is kept for failures that are not
   * about one field — a refusal from the server, a network error.
   */
  const [newModelError, setNewModelError] = useState<string | null>(null);
  /*
   * Whether the key has been edited by hand. While it is false the name seeds the key; the
   * first keystroke in the key field ends that for the life of the panel, so a typed key is
   * never overwritten by a later edit to the name.
   */
  const [keyTouched, setKeyTouched] = useState(false);
  /*
   * THE MODEL'S OWN ATTRIBUTES — owner instruction, 2026-10-05: *"Also add Button Save Model when
   * we are chaning the model attributes."* A model's name and description are the only two fields
   * the server lets you change (§6.11 — `PATCH /capability-models/:id` takes `name` and
   * `description` and nothing else), and until now this screen had no way to reach either: a model
   * could be named on creation and never renamed.
   *
   * It is a right-side panel like every other form here (D-017) and its primary button says
   * **Save Model**, carrying the Save icon, so the control names the thing it writes — a screen
   * that also saves weights, capabilities and a baseline needs the difference stated.
   */
  const [modelAttrs, setModelAttrs] = useState<{
    id: string;
    name: string;
    description: string;
  } | null>(null);
  const [modelAttrsDirty, setModelAttrsDirty] = useState(false);

  /*
   * NAMING A VERSION — owner, 2026-10-05: *"I should be able to name my versions."*
   *
   * `POST /versions` has ALWAYS accepted a name and fallen back to the source's (§6.13), so the
   * generated-looking name was this screen's doing: it posted an empty body. Of the two halves of
   * the ruling, this was the broken one — renaming after the fact already works through the
   * attributes panel, which §6.11 stopped refusing on a baselined model (§6.19).
   *
   * A panel rather than a prompt, because it is a form (D-017), and it is seeded with the source's
   * name so leaving it untouched reproduces the server's own default exactly.
   *
   * ── AND IT CARRIES A DESCRIPTION NOW, SEEDED EMPTY. ──────────────────────────────────
   *
   * Owner, 2026-10-05: *"please enable the ability to create a version description"*. The field
   * already existed — `capability_model.description` is per row and a row is a version — so nothing
   * was added to the schema; §6.13 simply never asked for it and copied the source's.
   *
   * THE TWO FIELDS ARE SEEDED DIFFERENTLY ON PURPOSE, and this is the one decision in this panel:
   *
   *   * `name` is seeded with the SOURCE'S name. A name identifies the model across its versions,
   *     so carrying it over is correct, and an untouched field reproduces the server's own default.
   *   * `description` is seeded EMPTY. A per-version description exists to say what CHANGED in this
   *     version, and the source's description is a statement about the PREVIOUS one — copying it
   *     forward would be the same fault the name had before it was sent, where every version came
   *     out wearing its predecessor's text and reading as generated. The placeholder asks for the
   *     change, and leaving it blank leaves this version with no description rather than inheriting
   *     a false one.
   */
  const [newVersionDraft, setNewVersionDraft] = useState<{
    name: string;
    description: string;
  } | null>(null);

  /*
   * ── THE TWO WARNINGS, AND WHY THEY ARE HELD IN TWO DIFFERENT PLACES ──────────────────
   *
   * `usage` is the load-time answer to "has this model been used" (§6.9 returns it on the read), and
   * it is what decides whether an edit is previewed at all. With `used` false, NOTHING is added to
   * the edit path: no preview round trip, no dialog, no toast about consequences.
   *
   * `pendingWrite` is the IN-USE warning: a confirmation, before the write, carrying the server's
   * sentence and counts.
   *
   * `unbalanced` is the OTHER warning, and it is kept apart on screen as well as in the type —
   * §6.19: *"'This change recalculates 20 scores' and 'this model no longer totals 100%' are
   * different facts with different remedies"*. It is a band in the gate strip, not a question,
   * because the server does not refuse it and the owner asked to be warned rather than blocked.
   */
  const [usage, setUsage] = useState<CapabilityModelUsage | null>(null);
  const [pendingWrite, setPendingWrite] = useState<PendingWrite | null>(null);
  const [unbalanced, setUnbalanced] = useState<BaselinedModelUnbalancedWarning | null>(null);

  /**
   * Per-model delete previews for the models table, keyed by model id (§6.20's `?dryRun=1`).
   *
   * §6.10's version list does not carry usage — it is four columns about the model itself — so the
   * table asks the delete endpoint what it would do. A row with no entry here is a row whose preview
   * has not arrived or did not answer, and it gets the bin: the server is the guard, and refusing to
   * offer an action because a preview failed would be worse than offering one the server may refuse
   * in its own words.
   */
  const [deletePreviews, setDeletePreviews] = useState<
    Readonly<Record<string, CapabilityModelDeletePreview>>
  >({});

  /* auto-dismiss toast — `FrameworksList.tsx:214-219`, 3000 ms. */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const notifyOk = (message: string) => setToast({ type: 'success', message });
  const notifyErr = (message: string) => setToast({ type: 'error', message });

  /* 1 — context and the initiative selector's options. */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [contextPayload, listPayload] = await Promise.all([
          erpApi.context(),
          erpApi.initiatives.list({ limit: 200 }),
        ]);
        if (cancelled) return;
        setContext(contextPayload.context);
        setInitiatives(listPayload.initiatives);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* 2 — the chosen initiative's versions. */
  const loadVersions = useCallback(async () => {
    if (!initiativeId) {
      setVersions([]);
      return;
    }
    try {
      const payload = await erpApi.capabilityModels.listForInitiative(initiativeId);
      setVersions(payload.capability_models);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load this initiative’s models');
    }
  }, [initiativeId]);

  useEffect(() => {
    void loadVersions();
  }, [loadVersions]);

  /*
   * THE MODEL BEING EDITED IS THE URL'S, AND ONLY THE URL'S.
   *
   * It used to fall back to the newest version (`versions[0]`, §6.10 orders `version DESC`), so
   * choosing an initiative dropped you straight into a tree. DECISIONS.md D-018 changes that: the
   * models are RECORDS IN A TABLE and you open one from it, "rather than hiding behind a dropdown".
   * With no `?model=` in the URL this screen now shows that table.
   *
   * Nothing became unreachable. The table is ordered `version DESC` too, so the version that used
   * to open automatically is the first row, and `?model=` still opens any model directly — a
   * bookmarked or pasted link lands exactly where it used to.
   */
  const modelId = modelParam;
  /** The models table, rather than one model's tree. */
  const showModelList = initiativeId !== '' && modelParam === null;

  const seedFromPayload = useCallback((payload: CapabilityModelTreePayload) => {
    setTree(payload);
    setServerBalance(payload.weightBalance);
    setWeights(Object.fromEntries(payload.capabilities.map((c) => [c.id, c.weight_tenths])));
    setEditing(null);
    /* §6.9 sends `usage` on the read and nowhere else; the create and the version copy answer with a
       tree and nothing to count, and leaving the last model's usage in place would make the next
       model's first edit ask a question about this one. */
    setUsage(payload.usage ?? null);
    /* A READ warns about nothing (§6.19), so this clears on every reload — which is correct: the
       imbalance itself stays on screen in the gate strip's own live reading. */
    setUnbalanced(unbalancedWarningOf(payload.warnings));
  }, []);

  /* 3 — the tree. */
  const loadTree = useCallback(async () => {
    if (!modelId) {
      setTree(null);
      setServerBalance(null);
      setWeights({});
      setUsage(null);
      setUnbalanced(null);
      return;
    }
    setTreeLoading(true);
    setError(null);
    try {
      const payload = await erpApi.capabilityModels.get(modelId);
      seedFromPayload(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load this capability model');
    } finally {
      setTreeLoading(false);
    }
  }, [modelId, seedFromPayload]);

  useEffect(() => {
    void loadTree();
  }, [loadTree]);

  const canEdit = canEditCapabilityModel(context);
  /** §6.20 — `erp_administrator` only, narrower than every other write on a model. */
  const canDelete = canDeleteCapabilityModel(context);
  const model = tree?.capability_model ?? null;
  /* No `baselined` flag: `0106` leaves `status` one reachable value, so a boolean derived from
     it is always true and every branch on it is dead. */
  /*
   * NO LONGER `&& !baselined` — API_CONTRACT §6.19, owner 2026-10-05. The five endpoints that used
   * to answer `409 capability_model_is_baselined` all save now, so a baselined model's tree is as
   * editable as a draft's and the gate below offers Create new version as a CHOICE rather than as
   * the only way through. What is left here is the role check and "is there a model at all".
   */
  const editable = canEdit && model !== null;

  /**
   * 4 — THE MODELS TABLE'S DELETE PREVIEWS (§6.20's `?dryRun=1`).
   *
   * Only where the user could delete at all: a non-administrator does not see the bin (§17.3 —
   * hidden, not disabled), so asking the question on their behalf would be a round trip per row for
   * an answer nothing reads, against an endpoint that would refuse them anyway.
   *
   * One request per row, and a row's failure is its own: `Promise.all` over per-row `try`/`catch` so
   * one 403 or one network error does not blank the whole table's answers.
   */
  useEffect(() => {
    if (!showModelList || !canDelete || versions.length === 0) {
      setDeletePreviews({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        versions.map(async (row) => {
          try {
            return [row.id, await erpApi.capabilityModels.previewDelete(row.id)] as const;
          } catch {
            /* Unknown rather than "deletable" or "blocked": the render treats a missing entry as a
               row to offer the bin on, and the server decides for real. */
            return null;
          }
        }),
      );
      if (cancelled) return;
      setDeletePreviews(
        Object.fromEntries(entries.filter((entry): entry is NonNullable<typeof entry> => entry !== null)),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [showModelList, canDelete, versions]);

  /*
   * `useMemo` so the empty-tree case is a STABLE array. `tree?.capabilities ?? []` builds a new
   * `[]` on every render, which makes every hook that depends on it re-run every render — and the
   * live balance, the row list and the sibling lookups all depend on it.
   */
  const capabilities = useMemo(() => tree?.capabilities ?? [], [tree]);

  /** The tree as the weight arithmetic sees it, with local edits applied. */
  const nodes: WeightNode[] = useMemo(
    () =>
      capabilities.map((capability) => ({
        id: capability.id,
        parentId: capability.parent_capability_id,
        name: capability.name,
        weightTenths: weights[capability.id] ?? capability.weight_tenths,
        sortOrder: capability.sort_order,
      })),
    [capabilities, weights],
  );

  /**
   * THE LIVE BALANCE, computed locally as the user types — no round trip (§3.1: "Client: runs the
   * package as the user types, to show the live running total").
   *
   * This is `describeBalance`, the SAME function the server runs on every weight write and on
   * baseline, so the per-group sentences here are byte-identical to the ones in the 409.
   * `serverBalance` is kept beside it rather than replacing it, because a difference between the
   * two would itself be worth seeing.
   */
  const liveBalance = useMemo(() => describeBalance(nodes), [nodes]);

  /**
   * The tree as `WeightedTree` takes it — a capability and a template line reduced to the one
   * shape they share (`0094` made them the same tree shape on purpose).
   *
   * `effective_weight_tenths` is passed THROUGH, never derived: §3.3 computes it on the server,
   * floored at each level, and refuses it as input. A template has no such field, which is why the
   * component takes `null` for it and the column is the template screen's one absence.
   */
  const treeRows: WeightedTreeRow[] = useMemo(
    () =>
      capabilities.map((capability) => ({
        id: capability.id,
        parentId: capability.parent_capability_id,
        code: capability.code,
        name: capability.name,
        weightTenths: capability.weight_tenths,
        weightLocked: capability.weight_locked,
        sortOrder: capability.sort_order,
        depth: capability.depth,
        childCount: capability.child_count,
        effectiveWeightTenths: capability.effective_weight_tenths,
      })),
    [capabilities],
  );

  /* ── Writes ───────────────────────────────────────────────────────────── */

  /**
   * Re-seed from a write's response.
   *
   * Every write endpoint in §6.14-§6.17 returns `{ capabilities, weightBalance }` — the WHOLE tree
   * and the new deficit, "because one insert changes `child_count`, `depth` and the group's
   * balance, and a client patching its local copy would get at least one of the three wrong"
   * (§6.14). None of them returns the model row, which has not changed, so it is carried over.
   */
  const applyWrite = (
    payload: {
      capabilities: Capability[];
      weightBalance: WeightBalanceReport;
      usage: CapabilityModelUsage;
      warnings: CapabilityModelWarning[];
    },
    current: CapabilityModelTreePayload,
  ) =>
    seedFromPayload({
      capability_model: current.capability_model,
      capabilities: payload.capabilities,
      weightBalance: payload.weightBalance,
      /* The write's own answers, so the next edit asks the question against what is now true: a
         delete can take the model's last scored cell, and the usage that decides whether the next
         edit warns has to move with it. */
      usage: payload.usage,
      warnings: payload.warnings,
    });

  /**
   * ── THE EDIT GUARD. The whole of §6.19's client side is here. ─────────────────────────
   *
   * `commit` is the write. `preview` is the same write with `?dryRun=1`, which returns the warnings
   * the commit would return and writes nothing.
   *
   * **AN UNUSED MODEL TAKES THE SHORT PATH AND IS NOT PREVIEWED AT ALL.** The owner: *"If that model
   * hasn't been used, then I should see no impact at all."* `usage.used` is on the load (§6.9), so
   * the screen already knows, and an unused model's edit is byte-for-byte the request it always was —
   * no extra round trip to notice, nothing to dismiss.
   *
   * Where the model HAS been used, the preview runs first and the confirmation carries the SERVER's
   * sentence. If the preview comes back with nothing to say after all — a race, or a change the
   * server does not consider worth a warning — the commit just happens: the dialog exists to carry a
   * warning, not to add a step.
   */
  const guarded = async (
    dialog: Omit<PendingWrite, 'warnings' | 'commit'>,
    preview: () => Promise<CapabilityModelEditPreview>,
    commit: () => Promise<void>,
  ) => {
    if (usage?.used !== true) {
      await commit();
      return;
    }
    setBusy(true);
    let previewed: CapabilityModelEditPreview;
    try {
      previewed = await preview();
    } catch (err) {
      /* A refusal is a refusal whether it arrives on the preview or on the commit, and the preview
         runs every line that can refuse — so this is the server's own wording, shown now rather
         than after a write that would have failed the same way. */
      notifyErr(err instanceof Error ? err.message : 'Could not check that change');
      dialog.undo?.();
      return;
    } finally {
      setBusy(false);
    }
    if (previewed.warnings.length === 0) {
      await commit();
      return;
    }
    setPendingWrite({ ...dialog, warnings: previewed.warnings, commit });
  };

  /** Put local state back when a confirmation is answered with no. */
  const reseedWeights = () =>
    setWeights(Object.fromEntries(capabilities.map((c) => [c.id, c.weight_tenths])));

  /** §6.17 — the ONE weight write. An unbalanced set saves; the response carries the deficit. */
  const putWeights = async (changes: readonly WeightChange[]) => {
    const current = tree;
    if (!modelId || !current || changes.length === 0) return;
    setBusy(true);
    try {
      const payload = await erpApi.capabilityModels.putWeights(modelId, [...changes]);
      applyWrite(payload, current);
    } catch (err) {
      /* The server's own wording (§1.6), and the local state is put back from the tree so the
         screen never shows a weight the server refused. */
      notifyErr(err instanceof Error ? err.message : 'Could not save that weight');
      await loadTree();
    } finally {
      setBusy(false);
    }
  };

  /** The weight write, through the §6.19 guard — one entry from a cell, a whole set from a helper. */
  const saveWeights = async (changes: readonly WeightChange[]) => {
    if (!modelId || changes.length === 0) return;
    await guarded(
      {
        title: changes.length === 1 ? 'Save this weight?' : 'Save these weights?',
        confirmLabel: 'Save change',
        /* A weight is in local state before it is sent (§3.1's live total), so a no has to put the
           cell back rather than leave the screen showing a number nobody saved. */
        undo: reseedWeights,
      },
      () => erpApi.capabilityModels.previewPutWeights(modelId, [...changes]),
      () => putWeights(changes),
    );
  };

  /**
   * TYPING MOVES THE TOTAL. §6.1 asks for a LIVE running total, and API_CONTRACT §3.1 is explicit
   * about which side computes it: *"Client: runs the package as the user types, to show the live
   * running total. No round trip."*
   *
   * So a keystroke updates `weights` — which every total on the screen is derived from — while
   * `editing` keeps the raw text so "4." and "" remain typable. An earlier pass updated `weights`
   * only on blur, and the total then sat still while somebody typed into the cell above it: the
   * screen looked right and the one behaviour §6.1 insists on was missing.
   *
   * Nothing is SAVED here. The write happens on blur (`commitWeight`), so a half-typed number
   * never reaches the server.
   */
  const typeWeight = (row: WeightedTreeRow, text: string) => {
    setEditing({ id: row.id, text });
    const tenths = textToTenths(text);
    if (tenths === null) return;
    /*
     * AN OUT-OF-RANGE NUMBER IS NOT PUT INTO THE RUNNING TOTAL, AND IS STILL SENT.
     *
     * The package guards its inputs — `checkBalance` runs `assertPctTenths` and THROWS on anything
     * outside `0..1000` (`units.ts`), which is right for a scoring library and fatal in a render
     * path: typing "150" would take the screen down. So the live total only ever sums weights that
     * are in range, while `commitWeight` still SENDS what was typed, so §6.17 answers
     * `weight_out_of_range` and the user reads the server's own refusal. Clamping here would hide a
     * typo the server is about to name.
     */
    if (!isWeightInRange(tenths)) return;
    setWeights((prev) => ({ ...prev, [row.id]: tenths }));
  };

  const commitWeight = async (row: WeightedTreeRow, text: string) => {
    setEditing(null);
    const tenths = textToTenths(text);
    if (tenths === null) {
      /* Not a number: put the cell back. §6.17 range-checks on the server, so an out-of-range
         number IS sent and refused by name — only unparseable text is dropped here. */
      setWeights((prev) => ({ ...prev, [row.id]: row.weightTenths }));
      return;
    }
    if (tenths === row.weightTenths) return;
    setWeights((prev) => ({ ...prev, [row.id]: tenths }));
    await saveWeights([{ capability_id: row.id, weight_tenths: tenths }]);
  };

  /*
   * §3.2's two helpers — Distribute evenly and Normalize proportionally — now run inside
   * `WeightedTree`, which hands back the full replacement set for THIS screen to write through the
   * one weights endpoint. They are deliberately not endpoints at all, because "a server route for
   * them would be a second place the remainder rule lives, and the remainder rule is precisely the
   * thing that must have exactly one implementation."
   */

  /** §6.15 — `weight_locked` is persisted state, not a computation (§3.2). */
  const patchCapability = async (
    row: WeightedTreeRow,
    body: CapabilityPatch,
    failure: string,
  ) => {
    const current = tree;
    if (!modelId || !current) return;
    setBusy(true);
    try {
      const payload = await erpApi.capabilityModels.patchCapability(modelId, row.id, body);
      applyWrite(payload, current);
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : failure);
      await loadTree();
    } finally {
      setBusy(false);
    }
  };

  /*
   * THE LOCK AND THE RENAME GO THROUGH THE GUARD TOO, because the SERVER warns on them.
   *
   * §6.19 put the warning on all five endpoints and the predicate is the model's usage rather than
   * what the request changes, so `PATCH …/capabilities/:id` answers with the in-use warning whether
   * it moved a name, a padlock or neither. The screen follows the server rather than deciding for
   * itself which edits are consequential: the contract proves the warning byte-identical across
   * rename, add-area, add-child, reweight and capability-delete, and a screen that suppressed it for
   * two of them would be the second opinion §6.19 exists to avoid.
   */
  const toggleLock = async (row: WeightedTreeRow) => {
    if (!modelId) return;
    const body: CapabilityPatch = { weight_locked: !row.weightLocked };
    await guarded(
      { title: row.weightLocked ? 'Unlock this weight?' : 'Lock this weight?', confirmLabel: 'Save change' },
      () => erpApi.capabilityModels.previewPatchCapability(modelId, row.id, body),
      () => patchCapability(row, body, 'Could not change that lock'),
    );
  };

  const renameCapability = async (row: WeightedTreeRow, name: string) => {
    if (!modelId || name.trim() === row.name || !name.trim()) return;
    const body: CapabilityPatch = { name: name.trim() };
    await guarded(
      {
        title: 'Save this name?',
        confirmLabel: 'Save change',
        /* The cell is an uncontrolled input; reloading the tree is what puts the typed text back to
           the server's name, because `WeightedTree` keys the field on it. */
        undo: () => void loadTree(),
      },
      () => erpApi.capabilityModels.previewPatchCapability(modelId, row.id, body),
      () => patchCapability(row, body, 'Could not rename that capability'),
    );
  };

  /**
   * §6.14 — no weight is sent: a new capability starts at 0.0%, and sending one is refused.
   *
   * **AND ADDING A ROW DOES NOT UNBALANCE THE GROUP IT JOINS**, which this screen used to assert that
   * it did. §6.19's verified note: a new functional area joins a group already totalling 100.0% at
   * 0.0%, and that group STILL totals 1000 tenths. What unbalances the model is a row that creates a
   * NEW sibling group — the first child under a parent that had none, or the first functional area in
   * an empty model — because that group then totals 0.
   *
   * So the toast says which of the two actually happened. The old one said "the group is now under by
   * that much" after every add, which was wrong in the common case and is exactly the kind of
   * sentence a user calibrates against and then stops reading.
   */
  const addCapability = async () => {
    const current = tree;
    if (!modelId || !current || !newCapability) return;
    if (!newCapability.name.trim()) {
      notifyErr('Name is required');
      return;
    }
    const body: CapabilityCreate = {
      parent_capability_id: newCapability.parent_capability_id || null,
      code: newCapability.code.trim() || null,
      name: newCapability.name.trim(),
      description: null,
      sort_order: 0,
    };
    /* Whether this add CREATES a sibling group, which is the only case that moves a total. Read off
       the tree in hand — `child_count` is the server's own figure (§6.9). */
    const parentRow =
      body.parent_capability_id === null
        ? null
        : (capabilities.find((c) => c.id === body.parent_capability_id) ?? null);
    const createsGroup =
      body.parent_capability_id === null
        ? capabilities.length === 0
        : parentRow !== null && parentRow.child_count === 0;
    const commit = async () => {
      setBusy(true);
      try {
        const payload = await erpApi.capabilityModels.addCapability(modelId, body);
        applyWrite(payload, current);
        setNewCapability(null);
        notifyOk(
          createsGroup
            ? 'Added at 0.0%. It is the first in its group, so that group now totals 0.0% — allocate it before baselining.'
            : 'Added at 0.0%. Nothing was taken from its siblings, so that group still totals what it did.',
        );
      } catch (err) {
        notifyErr(err instanceof Error ? err.message : 'Could not add that capability');
      } finally {
        setBusy(false);
      }
    };
    await guarded(
      { title: 'Add this capability?', confirmLabel: 'Add' },
      () => erpApi.capabilityModels.previewAddCapability(modelId, body),
      commit,
    );
  };

  /**
   * §6.16 — the freed weight is left unallocated, and the response reports the new deficit.
   *
   * A CENTRED DIALOG rather than `window.confirm`, which is what the clone of
   * `FrameworkBuilder.tsx:647` put here. The question is unchanged, word for word; what changed is
   * that it now has to carry the server's warnings beside it when the model has been used, and two
   * confirmation mechanisms for one click — a native box and then a dialog — is the worse answer.
   * `ConfirmDialog` is D-017's and the module already uses it (`BusinessUnitHierarchy.tsx:529`).
   */
  const deleteCapability = async (row: WeightedTreeRow) => {
    const current = tree;
    if (!modelId || !current) return;
    const intro = `Delete “${row.name}”? Everything beneath it goes too, and its weight is left unallocated rather than shared out.`;
    const commit = async () => {
      setBusy(true);
      try {
        const payload = await erpApi.capabilityModels.removeCapability(modelId, row.id);
        applyWrite(payload, current);
        notifyOk('Deleted. Its weight is now unallocated.');
      } catch (err) {
        /* §6.16's guard names the counts; the message is shown as the server wrote it. */
        notifyErr(err instanceof Error ? err.message : 'Could not delete that capability');
      } finally {
        setBusy(false);
      }
    };
    /* The confirmation is asked whether or not the model has been used — a delete is destructive on
       its own account. The PREVIEW is what is skipped on an unused model, so an unused model's
       dialog is exactly the one sentence it always was. */
    if (usage?.used !== true) {
      setPendingWrite({
        title: 'Delete this capability?',
        intro,
        confirmLabel: 'Delete',
        destructive: true,
        warnings: [],
        commit,
      });
      return;
    }
    setBusy(true);
    try {
      const previewed = await erpApi.capabilityModels.previewRemoveCapability(modelId, row.id);
      setPendingWrite({
        title: 'Delete this capability?',
        intro,
        confirmLabel: 'Delete',
        destructive: true,
        warnings: previewed.warnings,
        commit,
      });
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Could not delete that capability');
    } finally {
      setBusy(false);
    }
  };

  /*
   * THE `baseline` HANDLER IS GONE (`0106`), with its button and its endpoint. A model is in
   * force from creation, so there was no status left for it to move.
   *
   * Worth keeping from its history: it used to re-seed the screen from `payload.capabilities`,
   * a key §6.12 never sent, so the Baseline button took the screen down instead of baselining
   * it. The fix was to re-seed from the tree already in hand. If a handler like this ever
   * returns, that is the trap.
   */

  /**
   * §6.13 — a new version; the source is left exactly as it is. (The "only a baselined model" clause went with `0106`: there is no other kind now.)
   *
   * **IT SENDS A NAME NOW.** The endpoint has always accepted one and fallen back to the source's,
   * and this call sent `{}` — which is why every version came out wearing its predecessor's name and
   * looked generated. The name comes from the panel; an untouched panel still sends the source's
   * name, so the server's own default is reproduced rather than bypassed.
   *
   * **AND A DESCRIPTION.** It is sent on EVERY call, including when it is blank, and that is what
   * makes the panel's empty seed mean something: an omitted key falls back to the source's
   * description (§6.13 keeps that for callers that do not know about the field), so a blank field
   * has to be sent as `null` to say "this version has no description" rather than "copy the
   * previous one's".
   */
  const newVersion = async () => {
    const draft = newVersionDraft;
    if (!modelId || draft === null) return;
    if (!draft.name.trim()) {
      notifyErr('Name is required');
      return;
    }
    setBusy(true);
    try {
      const payload = await erpApi.capabilityModels.newVersion(modelId, {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
      });
      setNewVersionDraft(null);
      await loadVersions();
      setSearchParams({ initiative: initiativeId, model: payload.capability_model.id });
      notifyOk(payload.affectedAssessments.message);
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Could not create a new version');
    } finally {
      setBusy(false);
    }
  };

  const createModel = async () => {
    if (!newModel) return;
    setNewModelError(null);
    if (!newModel.initiativeId) {
      setNewModelError('Choose an initiative for this model.');
      return;
    }
    if (!newModel.name.trim()) {
      setNewModelError('Name is required.');
      return;
    }
    /*
     * The key is checked HERE as well as by the server, because the database's own CHECK would
     * come back as a constraint name rather than a sentence. The shape is `0105`'s: lower-case
     * letters, digits and hyphens, 2-64 characters, no leading or trailing hyphen.
     */
    /* NORMALISED, not refused — see `../keys.ts`. */
    const modelKey = finishKey(newModel.modelKey);
    const keyProblem = describeKeyProblem(newModel.modelKey);
    if (keyProblem !== null) {
      setNewModelError(keyProblem);
      return;
    }
    setBusy(true);
    try {
      const payload = await erpApi.capabilityModels.create(newModel.initiativeId, {
        model_key: modelKey,
        name: newModel.name.trim(),
        /* Blank is `null`, not `''` — the column is nullable and the models table tests the value
           for truthiness before it renders the second line, so an empty string would reserve the
           row's description line for nothing. */
        description: newModel.description.trim() || null,
        ...(newModel.templateId ? { from_capability_template_id: newModel.templateId } : {}),
      });
      setNewModel(null);
      await loadVersions();
      setSearchParams({ initiative: initiativeId, model: payload.capability_model.id });
      notifyOk('Capability model created');
    } catch (err) {
      /* §6.8 refuses an unbalanced TEMPLATE with the same `weights_do_not_balance` body. Shown
         in the panel, which is still open and still holds everything that was typed. */
      setNewModelError(err instanceof Error ? err.message : 'Could not create that model.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Write the model's own name and description — §6.11, the only two fields it accepts.
   *
   * It does NOT touch weights, capabilities or the baseline: those have their own write paths and
   * their own buttons, which is exactly why this one is labelled **Save Model** rather than Save.
   * A baselined model accepts both fields (§6.11, and since §6.19 it is not refused on any ground),
   * which is the half of *"I should be able to name my versions"* that already worked: a name typed
   * wrongly at creation is corrected here.
   */
  const saveModelAttrs = async () => {
    const attrs = modelAttrs;
    if (!attrs) return;
    if (!attrs.name.trim()) {
      notifyErr('Name is required');
      return;
    }
    const body = {
      name: attrs.name.trim(),
      description: attrs.description.trim() || null,
    };
    const commit = async () => {
      setBusy(true);
      try {
        await erpApi.capabilityModels.patch(attrs.id, body);
        setModelAttrs(null);
        setModelAttrsDirty(false);
        /* The list carries the name, and the tree payload carries it too when this model is open. */
        await loadVersions();
        if (modelId === attrs.id) await loadTree();
        notifyOk('Capability model saved');
      } catch (err) {
        notifyErr(err instanceof Error ? err.message : 'Could not save this model');
      } finally {
        setBusy(false);
      }
    };
    /*
     * The guard reads the OPEN model's usage, and this panel also opens from the models table, where
     * no model is open. There the edit is committed directly: a name and a description are the two
     * fields that move no score, the server warns if it has something to say, and asking the preview
     * for a model whose usage this screen has not read would be a guess.
     */
    if (modelId !== attrs.id) {
      await commit();
      return;
    }
    await guarded(
      { title: 'Save this model?', confirmLabel: 'Save Model' },
      () => erpApi.capabilityModels.previewPatch(attrs.id, body),
      commit,
    );
  };

  /**
   * §6.20 — DELETE A MODEL NOTHING DEPENDS ON. The bin on the models table.
   *
   * A CONFIRMATION, so it is the centred dialog (D-017) and not a panel: there is nothing to fill in,
   * the action is already chosen, and it must interrupt. It names what is going and says the delete
   * is permanent, because it is — `0091`'s convention 5 makes every delete in this module HARD except
   * `applications`, so there is no tombstone and nothing to restore from. What outlives the rows is
   * the audit trail, which records the model, each capability, and — for a baselined model — that a
   * published artefact ceased to exist and was provably unused when it did.
   */
  const askDeleteModel = (row: CapabilityModelVersionRow) => {
    const preview = deletePreviews[row.id];
    const count = preview?.capabilityCount ?? row.capability_count;
    setPendingWrite({
      title: 'Delete capability model',
      intro:
        `Delete “${row.name}” v${row.version}? ` +
        (count === 0
          ? 'It has no capabilities. '
          : `Its ${count} ${count === 1 ? 'capability goes' : 'capabilities go'} with it. `) +
        'This cannot be undone — a deleted model is not recoverable.',
      confirmLabel: 'Delete',
      destructive: true,
      /* Nothing to warn about: the delete only happens when the model is unused (§6.20). */
      warnings: [],
      commit: async () => {
        setBusy(true);
        try {
          await erpApi.capabilityModels.remove(row.id);
          notifyOk(`Deleted “${row.name}” v${row.version}`);
          /* The list reload re-runs the preview effect, so the remaining rows' bins are re-asked —
             deleting version 3 is what unblocks version 2's `capability_model_is_superseded`. */
          await loadVersions();
        } catch (err) {
          /* §6.20's four refusals each name their own reason and carry every blocker; the first one
             is `message`, and it is shown as the server wrote it. */
          notifyErr(err instanceof Error ? err.message : 'Could not delete this capability model');
          await loadVersions();
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const openModelAttrs = (row: { id: string; name: string; description: string | null }) => {
    setModelAttrs({ id: row.id, name: row.name, description: row.description ?? '' });
    setModelAttrsDirty(false);
  };

  const openNewModel = async () => {
    /*
     * THE PANEL CARRIES THE INITIATIVE, so the control works on a first visit.
     *
     * Owner, 2026-10-05: *"I came to the screen Capability Model the very first time and I do not
     * see '+ Add New Model' only when I try to pull down the Initiative ... I need to have
     * ability to create a new model"*. The button was hidden until an initiative had been chosen
     * — true to the data (a model belongs to an initiative) and useless as an interface: the one
     * action someone arrives wanting was invisible until they had guessed at an unrelated control
     * first. The initiative is now the panel's first field, seeded from the picker when one is
     * already chosen.
     */
    setNewModel({ initiativeId, modelKey: '', name: '', description: '', templateId: '' });
    setNewModelError(null);
    setKeyTouched(false);
    try {
      /* No `active` filter: `0105` dropped the column, so every template is offered. */
      const payload = await erpApi.capabilityTemplates.list({ limit: 200 });
      setTemplates(payload.capability_templates);
    } catch {
      /* A template list that cannot be read is not an error state for this dialog: the model can
         still be created empty, which is §6.8's default. */
      setTemplates([]);
    }
  };

  /* ── Render ───────────────────────────────────────────────────────────── */

  const initiative = initiatives.find((row) => row.id === initiativeId) ?? null;
  const gateBalanced = liveBalance.balanced && capabilities.length > 0;
  const gateClass = capabilities.length === 0 ? ' blocked' : gateBalanced ? ' ready' : '';
  /* `gateReason` went with the Baseline button — it was that control's disabled-title. */

  return (
    <ErpScreen>
      <div className="page fb-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={CapabilityModelHelp} />
        <PageHeader
          title="Capability Model"
          subtitle="Functional areas, capabilities and their weights — every level totals exactly 100.0%"
        />

        {/*
          ── THE TWO PICKERS ARE PROJECT/OPPORTUNITY-CLASS FIELDS (D-014) ──────────────────
          Owner ruling, 2026-10-05: *"from UI design perspective, Initiative and version should
          be hanlded as Project and Opportunity."*

          The clone of `FrameworkBuilder.tsx:933-949` put a bare `<select class="fb-framework-
          select">` here, which was faithful and is now wrong: these are not framework pickers.
          They are the platform's ONE "choose a record" field — `SelectField`, 380px
          (`FIELD.widths.lg`), the id bold ahead of its description, one line cut with an
          ellipsis, never checkboxes — and it is REPLACED rather than restyled, for the reason
          `ScenarioBuilder.tsx:209-214` records of the identical ruling made once before:
          restyling "would have left two implementations of one control, which is how they drift
          apart". Importing `@platform/ui-kit` is not a D-003 breach — D-003 forbids
          `pages/apr/`, `pages/master-data/` and `server/src/apr/`, another product's folders;
          `packages/*` is the platform's shared layer, and this module already imports from it.

          THE BEHAVIOUR IS UNCHANGED. The choice still writes to `?initiative=&model=` and
          nothing else (the load effect keys on the URL, so it does the reload); the prompt row
          is still a real choice on the initiative field, which clears the query string and puts
          the screen back to asking; the newest version is still selected by default, upstream.
        */}
        <div className="fb-toolbar">
          <SelectField
            label="Initiative"
            width="lg"
            id="erp-cap-initiative"
            /* The toolbar is a centred flex row; the field's own label aligns to the top by
               default, which would sit it above the button beside it. */
            style={{ alignItems: 'center' }}
            value={initiativeId}
            placeholder={ERP_PROMPT.initiative}
            options={initiatives.map((row) => ({
              value: row.id,
              /* The initiative's NAME is what is scanned for here, so it takes the bold slot the
                 project id takes on a Project row — this module has no second human-readable
                 key, and the uuid is not one. */
              id: row.name,
              description: row.description,
            }))}
            onChange={(value) =>
              value ? setSearchParams({ initiative: value }) : setSearchParams({})
            }
          />

          {/* THE VERSION PICKER NOW APPEARS ONLY INSIDE A MODEL. In the models table it would be a
              second way to choose the thing the table is for — D-018: the models are records "rather
              than hiding behind a dropdown". It is KEPT for switching between versions without going
              back to the list, because the owner himself ruled it a record-picker field (D-014) and
              removing it was not asked for. */}
          {!showModelList && (
            <SelectField
              label="Version"
              width="lg"
              id="erp-cap-version"
              style={{ alignItems: 'center' }}
              disabled={!initiativeId || versions.length === 0}
              value={modelId ?? ''}
              /* An initiative with no model yet is an EMPTY field, not an unanswered one — the
               wording the native `<option>` carried. */
              placeholder={versions.length === 0 ? ERP_PROMPT.noVersionsYet : ERP_PROMPT.version}
              /* REQUIRED once an initiative is chosen, so no clear row: the native control had no
               way to return to "no version" either, and offering one would strip `?model=` from
               a URL the load effect reads. */
              allowClear={false}
              /*
               * THE PICKER SHOWS WHAT THE USER TYPED.
               *
               * Owner, 2026-10-05: *"I changed the version description and it is still stating
               * V1 - baseline"*. The second line carried only `status · N capabilities` — facts
               * the product already knows — so a version description could be written and then
               * appear nowhere, which reads as the edit not having saved.
               *
               * The status moves up beside the number, where it belongs: it qualifies WHICH
               * version this is. The second line is then the user's own sentence, falling back
               * to the capability count only when they have not written one, so the row is never
               * blank.
               */
              options={versions.map((version) => ({
                value: version.id,
                /*
                 * THE VERSION IS IDENTIFIED BY WHAT THE USER CALLED IT.
                 *
                 * Owner, 2026-10-05: *"I do not need to see V1 - All I want to see is MY
                 * version"*. The bold line carried `v1 · baselined` — the sequence number the
                 * server assigns and a status — so a version the user had deliberately named was
                 * identified by neither of the two things they wrote.
                 *
                 * The name leads. The status stays on the second line because it decides whether
                 * a version can still be edited freely, and the capability count only appears
                 * when there is no description to show instead.
                 */
                id: version.name,
                description:
                  version.description && version.description.trim() !== ''
                    ? `${version.status} · ${version.description}`
                    : `${version.status} · ${version.capability_count} capabilities`,
              }))}
              onChange={(value) => setSearchParams({ initiative: initiativeId, model: value })}
            />
          )}

          {/* Back to the models table. The only way out of a tree, so it is not optional. */}
          {!showModelList && initiativeId !== '' && (
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--ghost"
              onClick={() => setSearchParams({ initiative: initiativeId })}
            >
              <i className="material-icons">arrow_back</i>
              All models
            </button>
          )}

          {/* The model's own attributes, reachable from inside the tree as well as from the table —
              otherwise a model opened by URL could never be renamed. */}
          {canEdit && !showModelList && model !== null && (
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--ghost"
              onClick={() => openModelAttrs(model)}
            >
              <i className="material-icons">edit</i>
              Edit version
            </button>
          )}

          {/* ALWAYS SHOWN (owner, 2026-10-05). The panel asks which initiative, so arriving on a
              blank screen and creating a model is one click rather than a hunt. */}
          {canEdit && (
            <button type="button" className="btn btn-primary" onClick={() => void openNewModel()}>
              + New Model
            </button>
          )}
        </div>

        {initiative && (
          <div className="fb-framework-ribbon">
            {/* No "INITIATIVE" eyebrow — the owner's instruction, 2026-10-05: "I do not need
                'INITIATIVE' before Initiatove name itself". The name is the band's only subject,
                the breadcrumb above already says where you are, and a label naming the type of
                the one thing on the row tells a reader what they can already see. */}
            <span className="fb-framework-ribbon-name">{initiative.name}</span>
            {model && (
              <>
                {/* The version by the name its author gave it, not by its sequence number
                    (owner, 2026-10-05). The status stays: it is what decides whether this version
                    can still be edited freely, which the number does not. */}
                <span className="fb-framework-ribbon-label">Version</span>
                <span className="fb-framework-ribbon-name">{model.name}</span>
                <span className="fb-framework-ribbon-desc">
                  {model.status}
                  {model.description && model.description.trim() !== ''
                    ? ` · ${model.description}`
                    : ''}
                </span>
              </>
            )}
          </div>
        )}

        <div className="fb-container">
          {loading ? (
            <div className="fb-state">Loading…</div>
          ) : error ? (
            <div className="fb-state fb-state-error">
              <p>⚠️ {error}</p>
              <button type="button" className="btn btn-primary" onClick={() => void loadTree()}>
                Retry
              </button>
            </div>
          ) : !initiativeId ? (
            /* Distinct from "this initiative has no model": offering + New Model here would
               build into no initiative at all. `FrameworkBuilder.tsx:997-1007` makes the same
               distinction for the same reason. */
            <div className="fb-state">
              <p>Choose an initiative above to build its capability model.</p>
              <p style={{ fontSize: 13, color: '#5f6368', marginTop: 4 }}>
                Its functional areas, capabilities and weights appear here. Every level must total
                exactly 100.0% before the model can be baselined.
              </p>
            </div>
          ) : showModelList ? (
            /*
             * ── THE MODELS, AS RECORDS ────────────────────────────────────────────────────────
             *
             * DECISIONS.md D-018, the owner: *"use that [the Knowledge Library screen] as the
             * starting point to design screens … Apply that across all screen when multiple records
             * should be presented on single screen."* A capability model is a record with a name, a
             * status, a size and a verdict on its weights, and those are precisely the four columns
             * that pattern asks for. `../chrome/recordList.css` carries the classes and cites every
             * value to `pages/apr/settings/KnowledgeLibrary.tsx` — cloned, never imported.
             *
             * THE TREE INSIDE A MODEL IS STILL A TREE. D-018 is explicit about it, and so is the
             * reason: the 100% rule is defined OVER the hierarchy, so flattening it into this table
             * would throw away the structure the rule is about. Open a model and the tree is
             * unchanged — same indent, same toggles, same inline weights, same gate.
             *
             * THERE IS A BIN NOW, AND THERE DID NOT USED TO BE. D-018 recorded, correctly at the
             * time, that *"the API has no endpoint that deletes a capability model"* — so a bin would
             * have been a button with nothing behind it. `DELETE /capability-models/:id` exists
             * (§6.20, owner 2026-10-05: *"I need to have ability to delete baseline model if baseline
             * model has not been used anywhere"*), and the stale screen was the bug.
             *
             * It is still NOT offered where the server would refuse it. §6.20's preview answers that
             * per row, so a blocked row says what is in the way in the server's own count instead of
             * carrying a control that fails on press — the same choice the template library makes
             * with *"in use by 2"* (`CapabilityTemplateLibrary.tsx:869`). The ruling D-018 rested on
             * also still holds for the case it was about: a model that has been SCORED against is
             * superseded by a new version, and that is one of the four reasons the bin is withheld.
             */
            versions.length === 0 ? (
              <div className="erp-rl-empty">
                <div className="erp-rl-empty-icon">
                  <i className="material-icons">account_tree</i>
                </div>
                <h2 className="erp-rl-empty-title">
                  No capability model yet for {initiative?.name ?? 'this initiative'}
                </h2>
                <p className="erp-rl-empty-body">
                  Create one empty, or start from a template — a template is copied, never shared.
                </p>
                {canEdit && (
                  <button
                    type="button"
                    className="erp-rl-btn erp-rl-btn--primary"
                    style={{ marginTop: 12 }}
                    onClick={() => void openNewModel()}
                  >
                    <i className="material-icons">add</i>
                    New Model
                  </button>
                )}
              </div>
            ) : (
              <div className="erp-rl-table-wrapper">
                <table className="erp-rl-table">
                  <thead>
                    <tr>
                      <th>Model</th>
                      <th>Version</th>
                      <th>Status</th>
                      <th className="erp-rl-right">Capabilities</th>
                      <th>Weights</th>
                      <th className="erp-rl-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versions.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {/* The pattern's primary cell: the name, and its description beneath.
                              The name opens the model, which is the row's own action. */}
                          <button
                            type="button"
                            className="erp-rl-name-btn"
                            title="Open this model's capability tree"
                            onClick={() =>
                              setSearchParams({ initiative: initiativeId, model: row.id })
                            }
                          >
                            {row.name}
                          </button>
                          {row.description && (
                            <span className="erp-rl-desc">{row.description}</span>
                          )}
                        </td>
                        <td className="erp-rl-nowrap">v{row.version}</td>
                        <td>
                          {/* §1.9.2's measured pairs: a baselined model is done (success, 6.34:1),
                              a draft is not yet anything (neutral, 8.68:1). */}
                          <span
                            className={`erp-rl-badge ${
                              row.status === 'baselined'
                                ? 'erp-rl-badge--success'
                                : 'erp-rl-badge--neutral'
                            }`}
                          >
                            {row.status === 'baselined' ? 'Baselined' : 'Draft'}
                          </span>
                        </td>
                        <td className="erp-rl-num">{row.capability_count}</td>
                        <td>
                          {/* The server's own verdict, not one recomputed here. §3.3: a computed
                              field is the server's and this screen renders what it was given. */}
                          <span
                            className={`erp-rl-badge ${
                              row.weights_balanced
                                ? 'erp-rl-badge--success'
                                : 'erp-rl-badge--warning'
                            }`}
                            title={
                              row.weights_balanced
                                ? 'Every level totals exactly 100.0%'
                                : 'At least one level does not total 100.0% — it saves as a draft but cannot be baselined'
                            }
                          >
                            {row.weights_balanced ? 'Balanced' : 'Does not balance'}
                          </span>
                        </td>
                        <td>
                          <div className="erp-rl-actions">
                            {/* 👁️ ✏️ 🗑️ — the pattern's icon pool, in the platform's row order. */}
                            <button
                              type="button"
                              className="erp-rl-glyph erp-rl-glyph--view"
                              title="Open this model's capability tree"
                              aria-label={`Open ${row.name}`}
                              onClick={() =>
                                setSearchParams({ initiative: initiativeId, model: row.id })
                              }
                            >
                              👁️
                            </button>
                            {canEdit && (
                              <button
                                type="button"
                                className="erp-rl-glyph"
                                title="Edit this model's name and description"
                                aria-label={`Edit ${row.name}`}
                                onClick={() => openModelAttrs(row)}
                              >
                                ✏️
                              </button>
                            )}
                            {/*
                              THE BIN — §6.20, and it is HIDDEN where it would be refused.

                              Two conditions, both omissions rather than disabled states (§17.3):
                              `erp_administrator` only, and then only where the preview says the
                              delete would be accepted. Nothing is rendered in its place — the owner
                              ruled the explanatory note off this table; see the note above the file's
                              `BLOCKER` comment.

                              A row with NO preview yet DOES get the bin: the server is the guard, and
                              a confirmation can only ever be followed by the server's own answer.
                            */}
                            {canDelete && deletePreviews[row.id]?.deletable !== false && (
                              <button
                                type="button"
                                className="erp-rl-glyph erp-rl-glyph--delete"
                                title="Delete this capability model — permanently"
                                aria-label={`Delete ${row.name}`}
                                disabled={busy}
                                onClick={() => askDeleteModel(row)}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : treeLoading ? (
            <div className="fb-state">Loading capability model…</div>
          ) : (
            <>
              {/* The model-level gate. One filled primary, and it is Baseline (§1.9.3.1). */}
              <div className={`fb-gate${gateClass}`}>
                <div className="fb-gate__text">
                  <div className="fb-gate__title">
                    {capabilities.length === 0
                      ? 'This model is empty'
                      : gateBalanced
                        ? 'Every level totals exactly 100.0%'
                        : /* The server's own envelope sentence, from the same formatter. */
                          summariseBalance(liveBalance.groups)}
                  </div>
                  {/*
                    THE PROTECTION CLAIM IS GONE, and it is the sentence this screen was blocking the
                    owner with: *"Baselined — structure and weights are protected so existing scores
                    stay stable. Create a new version to change it."* §6.19 removed the refusal it
                    described, so the sentence was describing a rule the server no longer has.

                    What replaces it says the two things that ARE true: this one is editable, and
                    superseding it is the other way to change it. Nothing reassuring is added about
                    consequences, because an unused model has none to mention — the owner: *"If that
                    model hasn't been used, then I should see no impact at all."*
                  */}
                  {/*
                    NO DRAFT, SO NO "yet" — `0106`. Every model is in force from creation, and the
                    sentence now says what the weights mean for USING it rather than for a state
                    transition that no longer exists: a model that does not add up cannot be
                    assessed against, and that refusal happens on the assessment grid.
                  */}
                  <div className="fb-gate__sub">
                    {gateBalanced
                      ? 'This model can be assessed against. You can still change it here, or create a new version to supersede it and leave this one as earlier scores read it.'
                      : 'It saves as it is — an unbalanced model is a normal state while you build one. It cannot be assessed against until every level totals exactly 100.0%.'}
                  </div>
                  {!gateBalanced && liveBalance.groups.length > 0 && (
                    /* Listed, not counted, and in tree order — §3.1 promises the order and
                       `ApplicationMasterDataTab.tsx:803-804` is why it is a list. */
                    <ul className="fb-gate__groups">
                      {liveBalance.groups.map((group) => (
                        <li key={group.parentCapabilityId ?? 'functional-areas'}>
                          {group.parentName ?? 'Functional areas'}: {group.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* The server's own refusal, when it has spoken. Kept beside the live reading
                      rather than replacing it — they are produced by the same formatter, so a
                      difference between them would itself be worth seeing. */}
                  {serverBalance && !serverBalance.balanced && serverBalance.groups.length > 0 && (
                    <ul className="fb-gate__groups">
                      {serverBalance.groups.map((group) => (
                        <li key={`${group.parentCapabilityId ?? 'root'}-server`}>
                          {group.parentName ?? 'Functional areas'}: {group.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/*
                    THE SECOND WARNING, AND IT IS KEPT SEPARATE ON PURPOSE — §6.19.

                    "This change recalculates 20 scores" is a question asked before a write, and it
                    lives in the confirmation dialog. "This baselined model no longer totals 100.0%"
                    is a STATE, it has a different remedy, and the server does not refuse it — so it
                    is a band here rather than a step in the way. Merging the two would produce a
                    sentence that means neither.

                    It carries the server's own words, which say the part a running total cannot: a
                    baselined model that does not total 100% has every score computed from it weighed
                    against that total rather than against a whole model.
                  */}
                  {unbalanced && <p className="fb-gate__warning">{unbalanced.message}</p>}
                </div>
                <div className="fb-gate__actions">
                  {/*
                    THE BASELINE BUTTON IS GONE (`0106`), and so is its endpoint. A model is in
                    force from the moment it exists, so the control had no transition left to
                    make; leaving it would have been a button that 404s.

                    Its two real checks — not empty, and every level totals 100.0% — were not
                    dropped with it. They run when a model is ASSESSED against, which is where
                    being wrong has consequences. The band above says so.
                  */}
                  {/* Superseding is still a legitimate choice, and now available on any model
                      rather than only a baselined one — there is no other kind. */}
                  {canEdit && (
                    <button
                      type="button"
                      className="fb-helper"
                      disabled={busy}
                      onClick={() =>
                        /* Name seeded from the source, description seeded EMPTY — the long form is
                           at the state declaration. */
                        setNewVersionDraft({ name: model?.name ?? '', description: '' })
                      }
                    >
                      Create new version
                    </button>
                  )}
                  {/*
                    ONE ADD CONTROL, AT THE MODEL LEVEL — owner, 2026-10-05: *"+capability should be
                    added not on the line but on the model level"*. The per-row `+ capability` is gone
                    from this screen's tree (`WeightedTree`'s `onAddChild` is no longer passed).

                    It works because the panel it opens ALREADY asks for the parent, and that field is
                    what decides what is being created: no parent is a functional area, a parent makes
                    it a capability under that parent. One control, one panel, and no decision about
                    where to click before you have decided what you are making. The button, the
                    panel's heading and the parent field all say "capability" for that reason.
                  */}
                  {editable && (
                    <button
                      type="button"
                      className="fb-helper"
                      disabled={busy}
                      onClick={() =>
                        setNewCapability({ parent_capability_id: '', name: '', code: '' })
                      }
                    >
                      + Add capability
                    </button>
                  )}
                </div>
              </div>

              {capabilities.length === 0 ? (
                <div className="fb-state">
                  <p>No functional areas yet in {model?.name ?? 'this model'}.</p>
                  {editable && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() =>
                        setNewCapability({ parent_capability_id: '', name: '', code: '' })
                      }
                    >
                      + Add capability
                    </button>
                  )}
                </div>
              ) : (
                <WeightedTree
                  rows={treeRows}
                  weights={weights}
                  editing={editing}
                  editable={editable}
                  busy={busy}
                  itemColumnLabel="Capability"
                  itemNoun={{ one: 'capability', many: 'capabilities' }}
                  rootGroupLabel="Functional areas"
                  /* NO `onAddChild` AND NO `addChildLabel` — the owner moved the add control to the
                     model level (see the gate's button), so the per-row `+ capability` does not
                     render here. The template library still passes both and is unchanged. */
                  /* §3.3's figure, computed on the server and rendered here, never recomputed. */
                  showEffective
                  onTypeWeight={typeWeight}
                  onCommitWeight={(row, text) => void commitWeight(row, text)}
                  onRename={(row, name) => void renameCapability(row, name)}
                  /* §6.15 — a capability HAS a `weight_locked` column, so the padlock renders. */
                  onToggleLock={(row) => void toggleLock(row)}
                  onDelete={(row) => void deleteCapability(row)}
                  onApplyWeights={(changes) => void saveWeights(changes)}
                  onError={notifyErr}
                />
              )}
            </>
          )}
        </div>

        {/* New capability. A RIGHT-SIDE PANEL, which replaced the centred `.form-modal` that
            stood here — DECISIONS.md D-017, the owner: *"in general please replace all windows
            from this app by 'right panel'"*. The panel does not dim the screen, so the tree this
            capability is being added into stays readable while it is being filled in, which is
            the reason he asked for it. The fields, their order, their labels, their hints and the
            Add/Cancel wording are untouched: only the shell changed. */}
        {newCapability && (
          <FormPanel
            open
            /*
              ONE HEADING, because there is now ONE control that opens this panel and the PARENT FIELD
              is what distinguishes what is being made (owner, 2026-10-05). The heading used to flip
              between "New Capability" and "New Functional Area" with the parent, which was right when
              a per-row + chose the parent for you and is wrong now: the button says "Add capability",
              so a heading that said "New Functional Area" before anything had been typed would read as
              having landed in the wrong place. The word "functional area" is where the decision is —
              the Parent field's first option, "None: this is a functional area".
            */
            title="New Capability"
            onRequestClose={() => setNewCapability(null)}
            onSave={() => void addCapability()}
            busy={busy}
            saveLabel="Add"
            busyLabel="Adding…"
            /* Anything typed counts. The parent is NOT part of this test: it is seeded from the
               row the + was clicked on, so it is where the panel started rather than an edit. */
            dirty={newCapability.name.trim() !== '' || newCapability.code.trim() !== ''}
            discardMessage="Discard this new capability?"
          >
            <div className="form-group">
              <label htmlFor="erp-cap-name">Name *</label>
              <input
                id="erp-cap-name"
                type="text"
                value={newCapability.name}
                onChange={(e) =>
                  setNewCapability((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                placeholder="e.g., Order to Cash"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-cap-code">Code</label>
              <input
                id="erp-cap-code"
                type="text"
                value={newCapability.code}
                onChange={(e) =>
                  setNewCapability((prev) => (prev ? { ...prev, code: e.target.value } : prev))
                }
                placeholder="e.g., O2C"
              />
            </div>
            <div className="form-group">
              {/*
                    D-014, and this one was already inconsistent before the ruling: a PARENT
                    PICKER IN A DIALOG is exactly what `BusinessUnitHierarchy.tsx:612` already
                    does with `SelectField`, so this screen's bare `<select>` left the module
                    holding two implementations of one control — the failure
                    `ScenarioBuilder.tsx:209-214` describes. Converting it removes an
                    inconsistency rather than widening scope, and the in-module precedent fixes
                    every parameter (`width="full"`, `orientation="stacked"`), so nothing here is
                    invented.

                    `style={{ display: 'flex' }}` is the one thing the precedent does not carry:
                    `SelectField`'s root is `inline-flex`, so inside a block `.form-group` it
                    shrink-to-fits and `width="full"`'s `100%` resolves against a container whose
                    width is still unknown — the field comes out as wide as its text rather than
                    as wide as the form. Making the root block-level flex is what makes `full`
                    mean full. Logged in UI_GAPS.md §11.
                  */}
              <SelectField
                label="Parent"
                id="erp-cap-parent"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                value={newCapability.parent_capability_id}
                /* '' IS A REAL CHOICE here, with its own wording — "this is a functional
                       area" — so there is no clear row: a second blank row would duplicate it,
                       and the prompt row would carry `UNSET` into `parent_capability_id`. */
                allowClear={false}
                options={[
                  { value: '', label: ERP_PROMPT.noParentFunctionalArea },
                  ...orderTree(nodes).map((node) => ({
                    value: node.id,
                    /* The name takes the bold slot, and nothing is added beside it: the
                           native `<option>` showed the name alone and a port does not grow new
                           information on the way through. */
                    id: node.name,
                  })),
                ]}
                onChange={(value) =>
                  setNewCapability((prev) =>
                    prev ? { ...prev, parent_capability_id: value } : prev,
                  )
                }
              />
              {/*
                CORRECTED, 2026-10-05 — this hint used to say the group "will read as under by that
                much", and it does not. A new row joins at 0.0% and nothing is taken from its
                siblings, so a group that totalled 100.0% still totals 100.0%. The case that moves a
                total is a row that creates a NEW group: the first capability under a parent that had
                none. §6.19 records the same correction on the server side.
              */}
              <p className="fb-field-hint">
                A new capability starts at <strong>0.0%</strong>, and nothing is taken from its
                siblings to pay for it — so the group it joins still totals exactly what it did. What
                does move a total is the <strong>first</strong> capability under a parent that had
                none: its children are a new group, and that group totals 0.0% until you allocate it.
              </p>
            </div>
          </FormPanel>
        )}

        {/* New model. The second window the owner named by name — *"please replace window New
            Capability Model, by panel on the right side"* (D-017). Instantiating from a template
            COPIES; it does not share. */}
        {newModel && (
          <FormPanel
            open
            title="New Capability Model"
            onRequestClose={() => {
              setNewModel(null);
              setNewModelError(null);
            }}
            onSave={() => void createModel()}
            busy={busy}
            saveLabel="Create Model"
            busyLabel="Creating…"
            /* '' is the template field's own default ("— Start empty —"), so choosing one is an
               edit and leaving it is not. */
            dirty={
              newModel.initiativeId !== initiativeId ||
              newModel.modelKey.trim() !== '' ||
              newModel.name.trim() !== '' ||
              newModel.description.trim() !== '' ||
              newModel.templateId !== ''
            }
            discardMessage="Discard this new capability model?"
          >
            {/* The refusal sits at the TOP of the panel, above the first field, so it is in
                view the moment Create Model is pressed — wherever the form is scrolled to. */}
            {newModelError && (
              <p className="fb-panel-error" role="alert">
                {newModelError}
              </p>
            )}

            {/* THE FIRST FIELD: which initiative this model belongs to. A model cannot exist
                without one, so asking here is what lets the button work before anything is
                chosen on the screen behind. SelectField because an initiative is a record, and
                this module picks records with the platform's field (D-014). */}
            <div className="form-group">
              <SelectField
                label="Initiative"
                width="lg"
                orientation="stacked"
                style={{ display: 'flex' }}
                id="erp-new-model-initiative"
                placeholder={ERP_PROMPT.initiative}
                allowClear={false}
                value={newModel.initiativeId}
                onChange={(value) =>
                  setNewModel((prev) => (prev ? { ...prev, initiativeId: value } : prev))
                }
                options={initiatives.map((row) => ({
                  value: row.id,
                  id: row.name,
                  description: row.description ?? undefined,
                }))}
              />
            </div>
            {/*
              THE MODEL'S KEY — owner, 2026-10-07: *"we need that field in Capability Model"*,
              alongside the one Capability Templates already carries rather than instead of it.

              It sits ABOVE the name because it is the identifier and the name is the label, which
              is the order the template library's own attributes panel puts them in. A key is typed
              by hand and referred to by people, so the format is enforced in three places that
              agree: this field's hint, `createModel`'s check, and `0105`'s own CHECK constraint.

              ALL VERSIONS OF A MODEL SHARE ITS KEY. A new version copies it (§6.13) rather than
              asking again, because the key identifies the thing the versions are versions OF.
            */}
            <div className="form-group">
              <label htmlFor="erp-model-key">Key *</label>
              <input
                id="erp-model-key"
                type="text"
                value={newModel.modelKey}
                /* Tidied on the way in; the edges are finished on blur, so a hyphen can still
                   be typed and then typed after. `../keys.ts` carries the reasoning. */
                onChange={(e) => {
                  setKeyTouched(true);
                  setNewModel((prev) =>
                    prev ? { ...prev, modelKey: draftKey(e.target.value) } : prev,
                  );
                }}
                onBlur={() =>
                  setNewModel((prev) =>
                    prev ? { ...prev, modelKey: finishKey(prev.modelKey) } : prev,
                  )
                }
                placeholder="e.g., fy27-scm"
                required
              />
              <p className="fb-field-hint">
                Capitals and spaces are tidied as you type, so &ldquo;FY27 SCM&rdquo; becomes{' '}
                <code>fy27-scm</code>. Unique within this initiative, and{' '}
                <strong>every version of this model keeps it</strong>, so it identifies the model
                rather than one of its versions.
              </p>
            </div>
            <div className="form-group">
              <label htmlFor="erp-model-name">Name *</label>
              <input
                id="erp-model-name"
                type="text"
                value={newModel.name}
                /*
                 * THE NAME SEEDS THE KEY UNTIL THE KEY IS TOUCHED. This panel opens with both
                 * blank — there is no template to seed from, unlike the library's — so without
                 * this the key is a field you must invent an answer to. It stops following the
                 * moment somebody edits the key themselves, which is what `keyTouched` records;
                 * a seed that kept overwriting a typed value would be worse than no seed.
                 */
                onChange={(e) =>
                  setNewModel((prev) =>
                    prev
                      ? {
                          ...prev,
                          name: e.target.value,
                          modelKey: keyTouched ? prev.modelKey : suggestKeyFromName(e.target.value),
                        }
                      : prev,
                  )
                }
                placeholder="e.g., FY27 Capability Model"
                required
              />
            </div>
            {/* The same control as the attributes panel's description, because it is the same
                field — `capability_model.description`, §6.8 on create and §6.11 afterwards. It is
                optional, and a model created without one can be given one later. */}
            <div className="form-group">
              <label htmlFor="erp-model-description">Description</label>
              <textarea
                id="erp-model-description"
                rows={3}
                value={newModel.description}
                onChange={(e) =>
                  setNewModel((prev) => (prev ? { ...prev, description: e.target.value } : prev))
                }
                placeholder="What this model covers, and what it is for"
              />
              <p className="fb-field-hint">
                The description is shown beneath the model's name in the models table, so it is what
                tells one version from another at a glance.
              </p>
            </div>
            <div className="form-group">
              {/*
                    D-014. A capability template is a CATALOGUE RECORD with a name and a line
                    count — the same class of question as a parent or an initiative, differing
                    only in stakes — so it is the same field. `SelectField` carries a disabled
                    option (`SelectFieldOption.disabled`), so §6.8's rule that an unbalanced
                    template cannot be instantiated survives the conversion unchanged: the row is
                    listed, greyed, and refuses the click.
                  */}
              <SelectField
                label="Start from a template"
                id="erp-model-template"
                width="full"
                orientation="stacked"
                style={{ display: 'flex' }}
                value={newModel.templateId}
                /* '' is "— Start empty —", a real choice and §6.8's default — not a prompt.
                       No clear row, for the same two reasons as the Parent field above. */
                allowClear={false}
                options={[
                  { value: '', label: ERP_PROMPT.startEmpty },
                  ...templates.map((template) => ({
                    value: template.id,
                    id: template.name,
                    /* The same two facts the `<option>` carried, in the same order and the
                           same words. */
                    description: `${template.line_count} lines${
                      template.weights_balanced ? '' : ' · does not balance'
                    }`,
                    disabled: !template.weights_balanced,
                  })),
                ]}
                onChange={(value) =>
                  setNewModel((prev) => (prev ? { ...prev, templateId: value } : prev))
                }
              />
              <p className="fb-field-hint">
                A template is <strong>copied</strong>, not shared: editing this model never touches
                the template it came from. A template whose own weights do not balance cannot be
                instantiated, because the model it produced could not balance either.
              </p>
            </div>
          </FormPanel>
        )}

        {/* ── SAVE MODEL — the model's own attributes ──────────────────────────────────────
            Owner instruction, 2026-10-05: *"Also add Button Save Model when we are chaning the
            model attributes."* A right-side panel like every other form here (D-017), at the
            SINGLE width (`../chrome/panelWidths.css`), and its one filled primary says **Save
            Model** and carries the Save icon — this screen also saves weights, capabilities and a
            baseline, so the control has to name what it writes.

            The two fields are the only two the server accepts (§6.11). Nothing else about a model
            is editable here, and inventing a field the API refuses would be a control that fails
            on press. */}
        {modelAttrs !== null && (
          <FormPanel
            open
            /*
             * THE PANEL NAMES THE VERSION IT EDITS, not the model.
             *
             * Owner, 2026-10-05: *"I can only change the names of the model not version."* The
             * panel was already editing this version's own row — name and description are per
             * version, not per model — but it was titled with the model name, its fields said
             * "Name" and "Description", and its hint talked about the model. So it read as
             * editing the model as a whole, and a user had no way to tell that typing here
             * changed v1 and left v2 alone. The behaviour was right and the words were wrong.
             */
            title={(() => {
              const row = versions.find((v) => v.id === modelAttrs.id);
              return row ? `Edit version v${row.version}` : 'Edit version';
            })()}
            subtitle={(() => {
              const row = versions.find((v) => v.id === modelAttrs.id);
              const base = modelAttrs.name.trim() === '' ? 'Capability Model' : modelAttrs.name;
              return row ? `${base} · ${row.status}` : base;
            })()}
            onRequestClose={() => {
              setModelAttrs(null);
              setModelAttrsDirty(false);
            }}
            onSave={() => void saveModelAttrs()}
            busy={busy}
            saveLabel="Save Version"
            busyLabel="Saving…"
            dirty={modelAttrsDirty}
            discardMessage="Discard your changes to this version?"
          >
            <div className="form-group">
              <label htmlFor="erp-model-attrs-name">Version name *</label>
              <input
                id="erp-model-attrs-name"
                type="text"
                required
                value={modelAttrs.name}
                onChange={(e) => {
                  setModelAttrsDirty(true);
                  setModelAttrs((prev) => (prev ? { ...prev, name: e.target.value } : prev));
                }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-model-attrs-description">Version description</label>
              <textarea
                id="erp-model-attrs-description"
                rows={3}
                value={modelAttrs.description}
                onChange={(e) => {
                  setModelAttrsDirty(true);
                  setModelAttrs((prev) => (prev ? { ...prev, description: e.target.value } : prev));
                }}
              />
              <p className="fb-field-hint">
                This name and description belong to <strong>this version only</strong> — every
                other version keeps its own. Weights, capabilities and the baseline each have
                their own control on the screen behind.
              </p>
            </div>
          </FormPanel>
        )}

        {/* ── NAME THE VERSION — §6.13, owner 2026-10-05 ────────────────────────────────────
            *"I should be able to name my versions."* The endpoint has always taken a name; this
            screen never sent one, so every version inherited its predecessor's and looked generated.
            A panel like every other form here (D-017), seeded with the source's name so that leaving
            it untouched reproduces the server's own default exactly rather than working around it.

            Renaming LATER was already possible and still is — the attributes panel, §6.11, which
            §6.19 stopped refusing on a baselined model. Of the two halves of the ruling this was the
            broken one. */}
        {newVersionDraft !== null && (
          <FormPanel
            open
            title="New Version"
            subtitle={model ? `Copied from v${model.version}` : undefined}
            onRequestClose={() => setNewVersionDraft(null)}
            onSave={() => void newVersion()}
            busy={busy}
            saveLabel="Create Version"
            busyLabel="Creating…"
            /* The name is seeded with the source's, so an untouched name is not an edit; the
               description is seeded empty, so any text in it is. */
            dirty={
              newVersionDraft.name.trim() !== (model?.name ?? '').trim() ||
              newVersionDraft.description.trim() !== ''
            }
            discardMessage="Discard this new version?"
          >
            <div className="form-group">
              <label htmlFor="erp-version-name">Name *</label>
              <input
                id="erp-version-name"
                type="text"
                required
                value={newVersionDraft.name}
                onChange={(e) =>
                  setNewVersionDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                }
                placeholder="e.g., FY27 Capability Model v3"
              />
              <p className="fb-field-hint">
                The whole tree is copied into a <strong>new draft</strong>. This version is left
                exactly as it is, so the scores already computed against it stay readable.
              </p>
            </div>
            {/* THE DESCRIPTION OF A VERSION IS WHAT CHANGED IN IT, which is why this field starts
                empty rather than carrying the source's forward — the reasoning sits at the state
                declaration, and the placeholder says it to the person typing. */}
            <div className="form-group">
              <label htmlFor="erp-version-description">Description</label>
              <textarea
                id="erp-version-description"
                rows={3}
                value={newVersionDraft.description}
                onChange={(e) =>
                  setNewVersionDraft((prev) =>
                    prev ? { ...prev, description: e.target.value } : prev,
                  )
                }
                placeholder="What changed in this version, and why"
              />
              <p className="fb-field-hint">
                It starts empty on purpose: the previous version's description describes that
                version, not this one. It is shown beneath the name in the models table, so it is
                what tells the versions apart there. Leave it blank and this version simply has
                none — you can add one later from <strong>Save Model</strong>.
              </p>
            </div>
          </FormPanel>
        )}

        {/* ── ONE CONFIRMATION DIALOG, FOR EVERY QUESTION THIS SCREEN ASKS ──────────────────
            D-017: a form is a right-side panel, a yes-or-no question about an action already chosen
            is a CENTRED DIALOG, because it has to interrupt. `ConfirmDialog` is the platform's
            (`@platform/ui-kit`), imported rather than cloned.

            THE SERVER'S WARNINGS ARE RENDERED ONE BLOCK EACH and never joined into a sentence —
            §6.19: *"'This change recalculates 20 scores' and 'this model no longer totals 100%' are
            different facts with different remedies"*. The text is the server's `message`, unaltered,
            which is what keeps this dialog and the audit trail wording one change the same way; the
            counts beneath the in-use one are its own named fields, for the same reason. */}
        <ConfirmDialog
          open={pendingWrite !== null}
          title={pendingWrite?.title ?? ''}
          confirmLabel={pendingWrite?.confirmLabel ?? 'Confirm'}
          destructive={pendingWrite?.destructive}
          busy={busy}
          onCancel={() => {
            pendingWrite?.undo?.();
            setPendingWrite(null);
          }}
          onConfirm={() => {
            const pending = pendingWrite;
            setPendingWrite(null);
            void pending?.commit();
          }}
          message={
            pendingWrite === null ? (
              ''
            ) : (
              <>
                {pendingWrite.intro !== undefined && (
                  <p className="cme-confirm-intro">{pendingWrite.intro}</p>
                )}
                {pendingWrite.warnings.map((warning) => (
                  <div className="cme-warning" key={warning.warning}>
                    <p className="cme-warning-text">{warning.message}</p>
                    {warning.warning === 'capability_model_already_in_use' && (
                      /* The server's own counts, as named fields rather than parsed back out of the
                         sentence above — §6.19's reason for sending them separately. Each is listed
                         only when it is not zero: a zero is not a fact about this model. */
                      <ul className="cme-warning-counts">
                        {warning.assessmentUnitCount > 0 && (
                          <li>{warning.assessmentUnitCount} assessment units</li>
                        )}
                        {warning.assessmentCellCount > 0 && (
                          <li>{warning.assessmentCellCount} assessment cells</li>
                        )}
                        {warning.scoredUnitCount > 0 && (
                          <li>{warning.scoredUnitCount} scores recalculated</li>
                        )}
                        {warning.applicationMapCount > 0 && (
                          <li>{warning.applicationMapCount} application mappings</li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </>
            )
          }
        />

        {toast && (
          <div className={`fb-toast fb-toast-${toast.type}`} role="status">
            {toast.type === 'success' ? '✓ ' : '⚠ '}
            {toast.message}
          </div>
        )}
      </div>
    </ErpScreen>
  );
}
